import { SetServiceDependencies } from '../../../infra/startup/types';
import { IAuthProviderService } from '../../authProviderService';
import IAuthTokenService from '../../authTokenService/IAuthTokenService';
import { IGrantHistoryService } from './IGrantHistoryService';
import IApplicationService from '../../applicationService/IApplicationService';
import { GrantHistoryOptionsType, GrantHistoryType } from './types';
import { GrantHistoryRepository } from './GrantHistoryRepository';
import { AuthContextEnum } from '../../authTokenService';
import { GrantHistoryInputType } from './types';
import IGrantHistoryClient from '../../../clients/stratus/grants/grantsHistory/IGrantHistoryClient';
import { ITenantHandlerService } from '../../tenantHandler';
import { getTroposStackStringByNumber } from '../../../clients/stackUtils';
import { InternalService } from '../../../core/services';
import { PromiseHelper } from '../../../utils/PromiseHelper';
import { GrantsHookReturnType } from '@clientos/types';
import IEventService from '../../eventService/IEventService';
import EventNames from '../../../config/eventNames';

export class GrantHistoryService
  extends InternalService
  implements IGrantHistoryService
{
  private _authTokenService: IAuthTokenService;
  private _authProviderService: IAuthProviderService;
  private _applicationService: IApplicationService;
  private _grantHistoryRepository: GrantHistoryRepository;
  private _grantHistoryClient: IGrantHistoryClient;
  private _enabled: boolean;
  private _tenantHandlerService: ITenantHandlerService;
  private _eventService: IEventService;
  private defaultKey = 'default';
  private initializedAuthContext: Map<
    AuthContextEnum | typeof this.defaultKey,
    boolean
  > = new Map();
  private promiseHelper = new PromiseHelper();

  constructor(props?: GrantHistoryInputType) {
    super();
    this._enabled = props?.enabled || false;
  }

  public setDependencies({
    services,
    repositories,
    clients
  }: SetServiceDependencies): void {
    const {
      authProviderService,
      authTokenService,
      applicationService,
      tenantHandlerService,
      eventService
    } = services;
    const { grantHistoryRepository } = repositories;
    const { stratus } = clients;

    this._authProviderService = authProviderService;
    this._authTokenService = authTokenService;
    this._applicationService = applicationService;
    this._grantHistoryRepository = grantHistoryRepository;
    this._grantHistoryClient = stratus.grantHistoryClient;
    this._tenantHandlerService = tenantHandlerService;
    this._eventService = eventService;
    this._markAsReady();
  }

  private shouldUpdateCache(
    srcGrants: GrantHistoryType[],
    targetGrants: GrantHistoryType[]
  ): boolean {
    return JSON.stringify(srcGrants) !== JSON.stringify(targetGrants);
  }

  private getGrantsFromCache(authContext: AuthContextEnum): GrantHistoryType[] {
    return this._grantHistoryRepository.findOne(
      this._authTokenService.getSuffix(authContext)
    );
  }

  private saveGrantsToCache(
    grants: GrantHistoryType[],
    authContext: AuthContextEnum
  ): void {
    this._grantHistoryRepository.save(
      this._authTokenService.getSuffix(authContext),
      grants
    );
  }

  private async getGrantsFromClient(
    authContext: AuthContextEnum,
    offset: number = 0
  ): Promise<GrantHistoryType[]> {
    const limit = 50;

    const tenantId = this._tenantHandlerService?.getTenantId();
    const stackNumber = this._applicationService.getAuthStack();
    const stackName = getTroposStackStringByNumber(stackNumber);

    const grants = await this._grantHistoryClient?.getGrantHistory({
      stack: stackName,
      authProvider:
        this._authProviderService.createAuthProviderByAuthContextEnum(
          authContext
        ),
      offset,
      limit,
      tenantId
    });

    let allGrants: GrantHistoryType[] =
      grants?.results?.map((c) => ({
        grant: c.grant,
        level: c.level
      })) || [];

    const totalResultsLength = grants?.totalRecords || 0;
    const pageResultsLength = grants?.results?.length || 0;
    const hasMoreGrantsHistoryPages =
      totalResultsLength > limit && pageResultsLength === limit;

    if (hasMoreGrantsHistoryPages) {
      const newOffset = offset + limit;
      const nextGrants = await this.getGrantsFromClient(authContext, newOffset);
      allGrants = allGrants.concat(...nextGrants);
    }

    return allGrants;
  }

  public async checkGrantsHistory(
    grants: GrantHistoryType[],
    options?: GrantHistoryOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    const authContext =
      options?.authContext || this._authTokenService.getCurrentContext();

    const contents = this.getGrantsFromCache(authContext);
    return grants?.every?.((grantToCheck) =>
      contents?.some?.((cachedGrant: GrantHistoryType) => {
        const hasLevel = !!grantToCheck?.level;
        const levelTest = hasLevel
          ? grantToCheck.level === cachedGrant.level
          : true;
        return grantToCheck.grant === cachedGrant.grant && levelTest;
      })
    );
  }

  public async haveGrantsHistoryChanged(
    options?: GrantHistoryOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return false;

    const authContext =
      options?.authContext || this._authTokenService.getCurrentContext();

    const grants = await this.getGrantsFromClient(authContext);
    const cachedGrants = this.getGrantsFromCache(authContext);

    if (this.shouldUpdateCache(grants, cachedGrants)) {
      this.saveGrantsToCache(grants, authContext);
      return true;
    }

    return false;
  }

  public async init(options?: GrantHistoryOptionsType): Promise<void> {
    const contextKey = options?.authContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(contextKey);
    const isInitialized = this.initializedAuthContext.get(contextKey);

    if (!isInitialized) {
      this.initializedAuthContext.set(contextKey, true);
      if (promise === undefined) {
        promise = this.refresh(options);
        this.promiseHelper.setPendingPromise(contextKey, promise);
      }
    }

    await promise;
  }

  private async refreshAction(
    options?: GrantHistoryOptionsType
  ): Promise<void> {
    if (await this.haveGrantsHistoryChanged(options)) {
      this._eventService.publish(
        EventNames.shellGrantsHistoryChangedEventName,
        undefined
      );
    }
  }

  public async refresh(options?: GrantHistoryOptionsType): Promise<void> {
    const promiseKey = options?.authContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(promiseKey);
    if (this.isEnabled() && promise === undefined) {
      promise = this.refreshAction(options);
      this.promiseHelper.setPendingPromise(promiseKey, promise);
    }
    await promise;
  }

  public useReactCheckGrantsHistory(
    _React: any,
    grants: GrantHistoryType[],
    options?: GrantHistoryOptionsType
  ): GrantsHookReturnType {
    const [result, setResult] = _React.useState({
      data: undefined,
      error: undefined,
      loading: true
    });

    const isMountedRef = _React.useRef(true);
    const optionsRef = _React.useRef(options);
    const handleDataCountRef = _React.useRef(0);

    const handleData = _React.useCallback(async () => {
      const thisCount = handleDataCountRef.current + 1;
      handleDataCountRef.current = thisCount;

      await this.init(optionsRef.current);

      const newData = await this.checkGrantsHistory(grants, optionsRef.current)
        .then((data) => ({ data, error: undefined, loading: false }))
        .catch((error) => ({ data: undefined, error, loading: false }));

      if (!isMountedRef.current) {
        console.warn(`Component unmounted before receiving data`);
      } else if (thisCount !== handleDataCountRef.current) {
        console.warn(`Race condition detected, discarding stale data`);
      } else {
        setResult(newData);
      }
    }, []);

    _React.useEffect(() => {
      handleData();
    }, [
      handleData,
      JSON.stringify(grants),
      options?.authContext,
      options?.offset
    ]);

    _React.useEffect(() => {
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    return _React.useMemo(
      () => ({
        ...result,
        reload: () => handleData()
      }),
      [result, handleData]
    );
  }

  public isEnabled(): boolean {
    if (!this._enabled) {
      console.warn('Grants History: disabled');
      return false;
    }
    return true;
  }
}
